import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { FiltersService } from './filters.service';
import { CurrencyService } from './currency.service';
import { CurrencyDefinition, RecognizedCurrencyCode } from './ebr-api-client/ebr-api-client.g';
import { Alert } from 'src/app/global/types';
import { Guid } from 'guid-typescript';
import { AlertService } from './alert.service';
import { EbrApiClient } from './ebr-api-client/ebr-api-client.service';
import { UnitOfMeasureTypes, MeasurementSystemConversionRates } from 'src/app/core/services/ebr-api-client/ebr-api-client.g';
export type UomOption = 'Imperial' | 'Metric';

@Injectable({
  providedIn: 'root'
})
export class CurrencyUomService {

  private selectedCurrencySubject = new BehaviorSubject<string>('USD');
  selectedCurrency$ = this.selectedCurrencySubject.asObservable();

  private selectedUomSubject = new BehaviorSubject<UomOption>('Imperial');
  selectedUom$ = this.selectedUomSubject.asObservable();

  private currencyLoadingSubject = new BehaviorSubject<boolean>(false);
  isCurrencyLoading$ = this.currencyLoadingSubject.asObservable();

  private currencyOptionsSubject = new BehaviorSubject<CurrencyDefinition[]>([]);
  currencyOptions$ = this.currencyOptionsSubject.asObservable();

  // Displayed currency - only updates when loading completes
  private displayedCurrencySubject = new BehaviorSubject<string>('USD');
  displayedCurrency$ = this.displayedCurrencySubject.asObservable();

  // Keeps track of the previously selected currency to help
  // determine the conversion rate when it is requested.
  private previouslySelectedCurrency: string = 'USD';

  // Keeps track of the previously selected unit of measure to help
  // determine the conversion rate when it is requested.
  private previouslySelectedUom: UomOption = 'Imperial';

  private currenciesLoaded = false;

  readonly uomOptions: UomOption[] = ['Imperial', 'Metric'];

  private currentRegions: string[] = [];

  private currencySet = new Set(
    Object.values(RecognizedCurrencyCode)
  );

  constructor(
    private filtersService: FiltersService,
    private currencyService: CurrencyService,
    private alertService: AlertService,
    private ebrApiClient: EbrApiClient
  ) {
    this.filtersService.selectedFiltersObservable.subscribe(filters => {
      if (filters?.regions && filters.regions.length > 0) {
        if (JSON.stringify(this.currentRegions) !== JSON.stringify(filters.regions)) {
          this.currentRegions = filters.regions;
          this.applyRegionDefaults(filters.regions);
        }
      }
    });
  }

  async loadCurrencies(): Promise<void> {
    if (this.currenciesLoaded) return;

    try {
      const currencies = await this.currencyService.getCurrencies();
      this.currencyOptionsSubject.next(currencies);
      this.currenciesLoaded = true;
    } catch (error) {
      console.error('Failed to load currencies', error);
      const alert: Alert = {
        id: Guid.create(),
        message: "Currency Conversion services are currently not working, data involving cross-regional contracts may be inaccurate. Please retry later.",
      };
      this.alertService.addAlert(alert);
    }
  }

  public async getUnitOfMeasurementSystemConversionRates(): Promise<MeasurementSystemConversionRates | undefined> {
    try {
      let data = await this.ebrApiClient.getUnitOfMeasurementConversionRates(this.previouslySelectedUom as UnitOfMeasureTypes,this.selectedUomSubject.getValue() as UnitOfMeasureTypes, undefined, undefined, undefined);
      return data.data;
    } catch (error) {
      console.error('Failed to load UnitOfMeasurementSystem conversion rates', error);
      const alert: Alert = {
        id: Guid.create(),
        message: "Failed to load UnitOfMeasurementSystem conversion rates, please retry later.",
      };
      this.alertService.addAlert(alert);
      return new MeasurementSystemConversionRates({
        fromMeasurementSystem: this.previouslySelectedUom as UnitOfMeasureTypes,
        toMeasurementSystem: this.selectedUomSubject.getValue() as UnitOfMeasureTypes,
        conversionRates: []
      });
    }
  }

  public async getCurrencyConversionRate(): Promise<number> {
    try {
      return await this.currencyService.getCurrencyConversionRate(this.toCurrency(this.previouslySelectedCurrency), this.toCurrency(this.selectedCurrencySubject.getValue()));
    } catch (error) {
      console.error('Failed to load currency conversion rate', error);
      const alert: Alert = {
        id: Guid.create(),
        message: "Failed to load currency conversion rate, please retry later.",
      };
      this.alertService.addAlert(alert);
      return 1; // Fallback to conversion rate of 1 on error
    }
  }

  private applyRegionDefaults(regions: string[]): void {
    const defaults = this.getDefaultsForRegions(regions);
    this.selectedCurrencySubject.next(defaults.currency);
    this.displayedCurrencySubject.next(defaults.currency);
    this.previouslySelectedCurrency = defaults.currency;
    this.previouslySelectedUom = defaults.uom;
    this.selectedUomSubject.next(defaults.uom);
  }

  getDefaultsForRegions(regions: string[]): { currency: string; uom: UomOption } {
    if (regions.length > 1) {
      return { currency: 'USD', uom: 'Imperial' };
    }

    const region = regions[0]?.toUpperCase();
    // UoM defaults to Metric only when the single selected region is LA or IMEA
    const uom: UomOption = (region === 'LA' || region === 'IMEA') ? 'Metric' : 'Imperial';
    if (region === 'EU') {
      return { currency: 'EUR', uom: 'Metric' };
    }
    return { currency: 'USD', uom };
  }

  setCurrency(currencyCode: string): void {
    // Store the currently selected currency before updating it
    this.previouslySelectedCurrency = this.selectedCurrencySubject.getValue();
    // Update the selected currency to the new value
    this.selectedCurrencySubject.next(currencyCode);
    this.displayedCurrencySubject.next(currencyCode);
  }

  setUom(uom: UomOption): void {
    // Store the currently selected UoM before updating it
    this.previouslySelectedUom = this.selectedUomSubject.getValue();
    this.selectedUomSubject.next(uom);
  }

  getCurrency(): string {
    return this.selectedCurrencySubject.getValue();
  }

  getUom(): UomOption {
    return this.selectedUomSubject.getValue();
  }

  searchCurrencies(searchTerm: string): CurrencyDefinition[] {
    const options = this.currencyOptionsSubject.getValue();
    if (!searchTerm?.trim()) {
      return options;
    }
    const term = searchTerm.toLowerCase().trim();
    return options.filter(c =>
      c.currencyCodeId.toLowerCase().includes(term) ||
      (c.currencyName ?? '').toLowerCase().includes(term)
    );
  }

  getCurrencyCode(): string {
    // Use displayed currency code for displaying in the UI
    const currencyCode = this.displayedCurrencySubject.getValue();
    return currencyCode;
  }

  setCurrencyLoading(loading: boolean): void {
    this.currencyLoadingSubject.next(loading);
    // When loading completes, sync displayed currency with selected currency
    if (!loading) {
      this.displayedCurrencySubject.next(this.selectedCurrencySubject.getValue());
    }
  }

  getDisplayedCurrency(): string {
    return this.displayedCurrencySubject.getValue();
  }

  toCurrency(value: string): RecognizedCurrencyCode {
    return this.currencySet.has(value as RecognizedCurrencyCode)
      ? (value as RecognizedCurrencyCode)
      : RecognizedCurrencyCode.Unknown;
  }
}
